import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Input, Button, Card, Select, Drawer, Space, Badge } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useEntries } from '../context/EntriesContext';

const DEFAULT_PAGE_SIZE = 25;
const MOBILE_BREAKPOINT_PX = 768;
const MOBILE_PAGE_SIZE = 10;

function filterBySearch(entries, q) {
  if (!q || !String(q).trim()) return entries;
  const lower = String(q).trim().toLowerCase();
  return entries.filter(
    (r) =>
      (r.player_name && String(r.player_name).toLowerCase().includes(lower)) ||
      (r.set_name && String(r.set_name).toLowerCase().includes(lower)) ||
      (r.tournament && String(r.tournament).toLowerCase().includes(lower)) ||
      (r.region && String(r.region).toLowerCase().includes(lower)) ||
      String(r.placement).includes(lower)
  );
}

const DECK_BUILDER_BASE = 'https://deckbuilder.egmanevents.com/?deck=';

function getDeckListUrl(deckList) {
  if (!deckList || !String(deckList).trim()) return null;
  const encoded = encodeURIComponent(String(deckList).trim());
  return `${DECK_BUILDER_BASE}${encoded}&type=optcg`;
}

function getTableColumns() {
  return [
    { title: 'Player', dataIndex: 'player_name', key: 'player_name', ellipsis: true },
    { title: 'Format', dataIndex: 'set_name', key: 'set_name', ellipsis: true },
    { title: 'Tournament', dataIndex: 'tournament', key: 'tournament', ellipsis: true },
    { title: 'Region', dataIndex: 'region', key: 'region', ellipsis: true },
    {
      title: 'Placement',
      dataIndex: 'placement',
      key: 'placement',
      align: 'center',
      width: 100,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.placement ?? 0) - (b.placement ?? 0),
    },
    {
      title: 'Deck List',
      key: 'deck_list',
      align: 'center',
      width: 110,
      render: (_, record) => {
        const url = getDeckListUrl(record.deck_list);
        if (!url) return '—';
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              fontSize: 13,
              fontWeight: 600,
              color: '#1677ff',
              backgroundColor: '#e6f4ff',
              borderRadius: 8,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            View deck
          </a>
        );
      },
    },
  ];
}

function DeckDetailPage() {
  const { deckName } = useParams();
  const navigate = useNavigate();
  const { entries: allEntries, loading, error } = useEntries();

  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedSets, setSelectedSets] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedTournaments, setSelectedTournaments] = useState([]);
  const [mobilePage, setMobilePage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_PAGE_SIZE);
  const loadMoreSentinelRef = useRef(null);

  useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth < MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const displayName = decodeURIComponent(deckName || '');
  const entriesForDeck = useMemo(() => {
    if (!allEntries.length || !displayName) return [];
    return allEntries.filter((e) => e.deck === displayName);
  }, [allEntries, displayName]);

  const filterOptions = useMemo(() => {
    const sets = [...new Set(entriesForDeck.map((e) => e.set_name).filter(Boolean))].sort();
    const regions = [...new Set(entriesForDeck.map((e) => e.region).filter(Boolean))].sort();
    const tournaments = [...new Set(entriesForDeck.map((e) => e.tournament).filter(Boolean))].sort();
    return { sets, regions, tournaments };
  }, [entriesForDeck]);

  const filteredData = useMemo(() => {
    let data = entriesForDeck;
    if (selectedSets.length > 0) {
      data = data.filter((e) => selectedSets.includes(e.set_name));
    }
    if (selectedRegions.length > 0) {
      data = data.filter((e) => selectedRegions.includes(e.region));
    }
    if (selectedTournaments.length > 0) {
      data = data.filter((e) => selectedTournaments.includes(e.tournament));
    }
    return filterBySearch(data, globalFilter);
  }, [entriesForDeck, selectedSets, selectedRegions, selectedTournaments, globalFilter]);

  const hasActiveFilters = selectedSets.length > 0 || selectedRegions.length > 0 || selectedTournaments.length > 0;
  const clearAllFilters = () => {
    setSelectedSets([]);
    setSelectedRegions([]);
    setSelectedTournaments([]);
    setGlobalFilter('');
    setMobilePage(1);
    setMobileVisibleCount(MOBILE_PAGE_SIZE);
  };

  const sortedForMobile = useMemo(() => {
    return [...filteredData].sort((a, b) => (a.placement ?? 0) - (b.placement ?? 0));
  }, [filteredData]);

  const pageSize = isNarrow ? MOBILE_PAGE_SIZE : DEFAULT_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(sortedForMobile.length / pageSize));
  const mobileStart = (mobilePage - 1) * pageSize;
  const mobileDataPaged = useMemo(
    () => sortedForMobile.slice(mobileStart, mobileStart + pageSize),
    [sortedForMobile, mobileStart, pageSize]
  );
  const mobileDataEndless = useMemo(
    () => sortedForMobile.slice(0, mobileVisibleCount),
    [sortedForMobile, mobileVisibleCount]
  );
  const mobileData = isNarrow ? mobileDataEndless : mobileDataPaged;
  const hasMoreMobile = isNarrow && mobileVisibleCount < sortedForMobile.length;

  useEffect(() => {
    if (!isNarrow && mobilePage > totalPages) setMobilePage(Math.max(1, totalPages));
  }, [mobilePage, totalPages, isNarrow]);

  useEffect(() => {
    setMobileVisibleCount(MOBILE_PAGE_SIZE);
  }, [selectedSets, selectedRegions, selectedTournaments, globalFilter]);

  const loadMore = useCallback(() => {
    setMobileVisibleCount((prev) => Math.min(prev + MOBILE_PAGE_SIZE, sortedForMobile.length));
  }, [sortedForMobile.length]);

  useEffect(() => {
    if (!isNarrow || !hasMoreMobile) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isNarrow, hasMoreMobile, loadMore]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        Loading tops for this deck...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', color: '#c33', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        <p>{error}</p>
        <Button type="primary" onClick={() => navigate('/')} style={{ marginTop: 12 }}>
          Back to Tournament Data
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '32px 40px 40px',
        maxWidth: '1600px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        maxHeight: '100vh',
        boxSizing: 'border-box',
        backgroundColor: '#f5f5f5',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Button type="primary" size="large" onClick={() => navigate('/')}>
          ← Back to Tournament Data
        </Button>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: '#333' }}>
          Tops for: {displayName}
        </h1>
        <span style={{ fontSize: 15, color: '#666' }}>
          {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
        </span>
      </div>

      {isNarrow ? (
        <>
          <div style={{ marginBottom: 16, flexShrink: 0 }}>
            <Badge count={hasActiveFilters ? (selectedSets.length + selectedRegions.length + selectedTournaments.length) : 0} size="small">
              <Button
                type="default"
                icon={<MenuOutlined />}
                onClick={() => setFilterDrawerOpen(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                Filters
              </Button>
            </Badge>
          </div>
          <Drawer
            title="Filters"
            placement="left"
            onClose={() => setFilterDrawerOpen(false)}
            open={filterDrawerOpen}
            width={Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 32 : 320)}
            styles={{ body: { paddingTop: 8 } }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Input.Search
                placeholder="Player, format, tournament, region, placement..."
                allowClear
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  setMobileVisibleCount(MOBILE_PAGE_SIZE);
                }}
                style={{ width: '100%' }}
              />
              <Select
                mode="multiple"
                placeholder="Format"
                value={selectedSets}
                onChange={(v) => { setSelectedSets(v); setMobileVisibleCount(MOBILE_PAGE_SIZE); }}
                options={filterOptions.sets.map((s) => ({ label: s, value: s }))}
                allowClear
                showSearch
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
              <Select
                mode="multiple"
                placeholder="Region"
                value={selectedRegions}
                onChange={(v) => { setSelectedRegions(v); setMobileVisibleCount(MOBILE_PAGE_SIZE); }}
                options={filterOptions.regions.map((r) => ({ label: r, value: r }))}
                allowClear
                showSearch
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
              <Select
                mode="multiple"
                placeholder="Tournament"
                value={selectedTournaments}
                onChange={(v) => { setSelectedTournaments(v); setMobileVisibleCount(MOBILE_PAGE_SIZE); }}
                options={filterOptions.tournaments.map((t) => ({ label: t, value: t }))}
                allowClear
                showSearch
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
              {hasActiveFilters && (
                <Button type="link" danger onClick={clearAllFilters} block>
                  Clear all filters
                </Button>
              )}
            </Space>
          </Drawer>
        </>
      ) : (
        <div
          style={{
            marginBottom: 20,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'row',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          <Input.Search
            placeholder="Player, format, tournament, region, placement..."
            allowClear
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setMobilePage(1);
            }}
            style={{ minWidth: 280, maxWidth: 420 }}
          />
          <Select
            mode="multiple"
            placeholder="Format"
            value={selectedSets}
            onChange={(v) => { setSelectedSets(v); setMobilePage(1); }}
            options={filterOptions.sets.map((s) => ({ label: s, value: s }))}
            allowClear
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            style={{ minWidth: 140 }}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            placeholder="Region"
            value={selectedRegions}
            onChange={(v) => { setSelectedRegions(v); setMobilePage(1); }}
            options={filterOptions.regions.map((r) => ({ label: r, value: r }))}
            allowClear
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            style={{ minWidth: 120 }}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            placeholder="Tournament"
            value={selectedTournaments}
            onChange={(v) => { setSelectedTournaments(v); setMobilePage(1); }}
            options={filterOptions.tournaments.map((t) => ({ label: t, value: t }))}
            allowClear
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            style={{ minWidth: 160 }}
            maxTagCount="responsive"
          />
          {hasActiveFilters && (
            <Button type="link" danger onClick={clearAllFilters}>
              Clear filters
            </Button>
          )}
        </div>
      )}

      {filteredData.length === 0 ? (
        <p style={{ color: '#666', flexShrink: 0 }}>
          {entriesForDeck.length === 0
            ? 'No tops found for this deck.'
            : hasActiveFilters || globalFilter.trim()
              ? 'No results match your filters or search.'
              : 'No results.'}
        </p>
      ) : isNarrow ? (
        <>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '4px 0',
            }}
          >
            {mobileData.map((e, i) => (
              <Card key={`${e.player_name}-${e.tournament}-${e.placement}-${i}`} size="small">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 12,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#0f1a2a' }}>{e.player_name}</span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#1677ff',
                      backgroundColor: '#e6f4ff',
                      padding: '4px 10px',
                      borderRadius: 8,
                    }}
                  >
                    #{e.placement}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#64748b' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ color: '#94a3b8' }}>Format:</span>
                    <span>{e.set_name}</span>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Tournament:</span>
                    <span style={{ marginLeft: 6 }}>{e.tournament}</span>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>Region:</span>
                    <span style={{ marginLeft: 6 }}>{e.region}</span>
                  </div>
                  {getDeckListUrl(e.deck_list) && (
                    <div>
                      <a
                        href={getDeckListUrl(e.deck_list)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          marginTop: 4,
                          padding: '6px 12px',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#1677ff',
                          backgroundColor: '#e6f4ff',
                          borderRadius: 8,
                          textDecoration: 'none',
                        }}
                      >
                        View deck
                      </a>
                    </div>
                  )}
                </div>
              </Card>
            ))}
            {hasMoreMobile && (
              <div
                ref={loadMoreSentinelRef}
                style={{
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#94a3b8',
                  fontSize: 13,
                }}
              >
                Loading more...
              </div>
            )}
            {!hasMoreMobile && sortedForMobile.length > MOBILE_PAGE_SIZE && (
              <div style={{ textAlign: 'center', padding: 12, color: '#94a3b8', fontSize: 13 }}>
                All {sortedForMobile.length} results shown
              </div>
            )}
          </div>
        </>
      ) : (
        <Card
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}
          bodyStyle={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="deck-detail-table-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table
              dataSource={filteredData.map((r, i) => ({ ...r, key: `${r.player_name}-${r.tournament}-${r.placement}-${i}` }))}
              columns={getTableColumns()}
              pagination={{
                pageSize: DEFAULT_PAGE_SIZE,
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `Showing ${range[0]}–${range[1]} of ${total}`,
                size: 'default',
              }}
              size="large"
              className="deck-detail-table"
            />
          </div>
        </Card>
      )}
    </div>
  );
}

export default DeckDetailPage;
